import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { type ReactNode, useEffect } from 'react';
import { PiCheckCircle, PiWarning } from 'react-icons/pi';
import { useParams } from 'wouter';
import { ActivityFeed } from '@/components/ActivityFeed/ActivityFeed';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { DeliverAction } from '@/components/client-portfolio/DeliverAction';
import { PortfolioList } from '@/components/client-portfolio/PortfolioList';
import Link from '@/components/Link';
import Loader from '@/components/Loader';
import { ApiError } from '@/lib/api';
import { TestBadge } from '@/components/TestBadge';
import { useBreadcrumbs } from '@/lib/breadcrumbs';
import { formatDate } from '@/lib/helpers';
import { MONDAY_BOARDS, mondayItemUrl } from '@/lib/monday';
import {
  apiErrorText,
  CLIENT_COMPANY_STATUS_COLORS,
  CLIENT_COMPANY_STATUS_LABELS,
  type ClientCompanyCard,
  type ClientCompanyStatus,
  requirementLabel,
  useChangeClientCompanyStatus,
  useClientCompany,
} from '@/models/clientCompanies';
import {
  CLIENT_COMPANIES_ALL,
  CLIENT_COMPANIES_DELIVER,
  CLIENT_COMPANIES_WRITE,
  useHasAnyPermission,
} from '@/models/permissions';
import { buildCompanyFeed } from '@/pages/client-companies/companyFeed';
import { CompanyDataCard } from '@/pages/client-companies/CompanyDataCard';
import { CompanyDocumentsCard } from '@/pages/client-companies/CompanyDocumentsCard';
import { CompanyPeopleCard } from '@/pages/client-companies/CompanyPeopleCard';

export default function ClientCompanyPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { data, isLoading, error } = useClientCompany(id);
  const { setBreadcrumbs } = useBreadcrumbs();
  const canWriteRole = useHasAnyPermission([
    CLIENT_COMPANIES_WRITE,
    CLIENT_COMPANIES_ALL,
    '*:*',
  ]);

  const name = data?.company.name;
  useEffect(() => {
    if (!name) return;
    setBreadcrumbs([
      { title: 'Client Companies', to: '/client-companies' },
      { title: name, to: `/client-companies/${id}` },
    ]);
    return () => setBreadcrumbs([]);
  }, [name, id, setBreadcrumbs]);

  const canDeliverRole = useHasAnyPermission([
    CLIENT_COMPANIES_DELIVER,
    CLIENT_COMPANIES_ALL,
    '*:*',
  ]);

  if (isLoading) return <Loader />;
  if (error || !data)
    return (
      <Paper p="md">
        {!error || (error instanceof ApiError && error.status === 404) ? (
          <Alert color="gray">
            Company not found, or it belongs to a client you do not work with.
          </Alert>
        ) : (
          <Alert color="red">{apiErrorText(error)}</Alert>
        )}
      </Paper>
    );

  const { company } = data;
  const updatedAt = company.updatedAt;
  const readOnly = data.readOnly === true;
  const canWrite = canWriteRole && !readOnly;
  const canDeliver = canDeliverRole && !readOnly;

  return (
    <Paper p="md" h="100%">
      <Container size="xl" px={0}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              <HeaderCard card={data} canWrite={canWrite} />
              {readOnly ? (
                <Alert color="gray" variant="light">
                  View only: what the client already has. Documents, comments,
                  files and history of the services are kept by Implementation.
                </Alert>
              ) : (
                <ReadinessCard card={data} />
              )}
              <PortfolioList
                services={data.services}
                canWrite={canWrite}
                readOnly={readOnly}
                actions={
                  canDeliver
                    ? (service) => (
                        <DeliverAction
                          service={service}
                          owner={{
                            kind: 'company',
                            registrationDate: company.registrationDate,
                            missing: data.requirements.missing,
                          }}
                        />
                      )
                    : undefined
                }
              />
              {!readOnly && (
                <ActivityFeed items={buildCompanyFeed(data.events)} />
              )}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md">
              <CompanyDataCard
                key={`data-${company.id}-${updatedAt}`}
                card={data}
                canWrite={canWrite}
              />
              <CompanyPeopleCard card={data} canWrite={canWrite} />
              {!readOnly && (
                <CompanyDocumentsCard card={data} canWrite={canWrite} />
              )}
              <MondayCard card={data} />
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
    </Paper>
  );
}

function HeaderCard({
  card,
  canWrite,
}: {
  card: ClientCompanyCard;
  canWrite: boolean;
}) {
  const { company, allowedStatuses } = card;
  const change = useChangeClientCompanyStatus();
  const status = company.status as ClientCompanyStatus;
  const contactName = [company.contactFirstName, company.contactLastName]
    .filter(Boolean)
    .join(' ');
  const canOpenContacts = useHasAnyPermission([
    'contacts:read',
    'contacts:read-all',
    'contacts:*',
    '*:*',
  ]);
  const canOpenOffers = useHasAnyPermission([
    'offers:read',
    'offers:fulfill',
    'offers:*',
    '*:*',
  ]);

  const askStatus = (next: ClientCompanyStatus) =>
    modals.openConfirmModal({
      title: 'Change company status',
      children: (
        <Text size="sm">
          {CLIENT_COMPANY_STATUS_LABELS[status]} -{' '}
          {CLIENT_COMPANY_STATUS_LABELS[next]}
          {next === 'liquidated' ? '. Liquidated is final.' : ''}
        </Text>
      ),
      labels: { confirm: 'Change', cancel: 'Cancel' },
      onConfirm: () =>
        change.mutateAsync({ id: company.id, status: next }).catch((e) =>
          notifications.show({
            color: 'red',
            title: 'Status not changed',
            message: apiErrorText(e),
          }),
        ),
    });

  return (
    <Card withBorder>
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Group gap="xs">
              <Title order={3}>{company.name}</Title>
              {company.isTest && <TestBadge />}
            </Group>
            {company.legalName && company.legalName !== company.name && (
              <Text c="dimmed" fz="sm">
                {company.legalName}
              </Text>
            )}
          </Stack>
          <Group gap="xs">
            <Badge
              size="lg"
              variant="light"
              color={CLIENT_COMPANY_STATUS_COLORS[status]}
            >
              {CLIENT_COMPANY_STATUS_LABELS[status] ?? status}
            </Badge>
            {canWrite && allowedStatuses.length > 0 && (
              <Select
                size="xs"
                w={180}
                placeholder="Change status"
                aria-label="Change status"
                value={null}
                data={allowedStatuses.map((s) => ({
                  value: s,
                  label: CLIENT_COMPANY_STATUS_LABELS[s],
                }))}
                onChange={(v) => v && askStatus(v as ClientCompanyStatus)}
                disabled={change.isPending}
              />
            )}
          </Group>
        </Group>
        <Group gap="lg">
          <Field label="Account">
            {company.accountId && canOpenContacts ? (
              <Link to={`~/accounts/${company.accountId}`}>
                {company.accountName}
              </Link>
            ) : (
              (company.accountName ?? '-')
            )}
          </Field>
          <Field label="Main contact">
            {company.contactId && canOpenContacts ? (
              <Link to={`~/contacts/${company.contactId}`}>
                {contactName || company.contactEmail || `#${company.contactId}`}
              </Link>
            ) : (
              contactName || company.contactEmail || '-'
            )}
          </Field>
          {company.sourceOfferId && (
            <Field label="Created by offer">
              {canOpenOffers ? (
                <Link to={`~/offers/${company.sourceOfferId}`}>
                  #{company.sourceOfferId}
                </Link>
              ) : (
                `#${company.sourceOfferId}`
              )}
            </Field>
          )}
          <Field label="Services">{String(card.services.length)}</Field>
        </Group>
      </Stack>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={0}>
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      <Text fz="sm" component="div">
        {children}
      </Text>
    </Stack>
  );
}

function ReadinessCard({ card }: { card: ClientCompanyCard }) {
  const { requirements } = card;
  if (requirements.complete)
    return (
      <Alert color="green" variant="light" icon={<PiCheckCircle />}>
        Company data is complete - services can be marked delivered.
      </Alert>
    );
  return (
    <Alert
      color="orange"
      variant="light"
      icon={<PiWarning />}
      title="Fill in before marking services delivered"
    >
      <Group gap={6}>
        {requirements.missing.map((key) => (
          <Badge key={key} size="sm" color="orange" variant="outline">
            {requirementLabel(key)}
          </Badge>
        ))}
      </Group>
    </Alert>
  );
}

function MondayCard({ card }: { card: ClientCompanyCard }) {
  const { company } = card;
  const extra = Object.entries(company.mondayExtra ?? {}).filter(
    ([, v]) => v != null && v !== '',
  );
  if (!company.mondayItemId && extra.length === 0) return null;
  return (
    <CollapsibleCard title="From Monday" defaultOpen={false}>
      <Table fz="xs" withRowBorders={false}>
        <Table.Tbody>
          {company.mondayItemId && (
            <Table.Tr>
              <Table.Td c="dimmed">Monday item</Table.Td>
              <Table.Td>
                <Anchor
                  fz="xs"
                  href={mondayItemUrl(
                    MONDAY_BOARDS.COMPANIES,
                    company.mondayItemId,
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  {company.mondayItemId}
                </Anchor>
              </Table.Td>
            </Table.Tr>
          )}
          {company.mondayGroup && (
            <Table.Tr>
              <Table.Td c="dimmed">Monday group</Table.Td>
              <Table.Td>{company.mondayGroup}</Table.Td>
            </Table.Tr>
          )}
          {extra.map(([k, v]) => (
            <Table.Tr key={k}>
              <Table.Td c="dimmed">{k}</Table.Td>
              <Table.Td>
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </Table.Td>
            </Table.Tr>
          ))}
          <Table.Tr>
            <Table.Td c="dimmed">Created in BO</Table.Td>
            <Table.Td>{formatDate(company.createdAt)}</Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </CollapsibleCard>
  );
}
